import React from 'react'
import { Form, Input, Select } from 'antd'
import { Box, Button, IconButton, Paper, Typography } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'

import { BankIconImg } from '../../../components/BankIconImg'
import { THAI_BANK_OPTIONS } from '../../../constants/thaiBanks'
import type {
  BlockPayload,
  IBankAccountRow,
  IBlockPayloadBankAccounts,
} from '../../../types'

const emptyRow = (): IBankAccountRow => ({
  bankName: '',
  accountName: '',
  accountNumber: '',
  branch: '',
})

interface Props {
  payload: IBlockPayloadBankAccounts
  onChange: (payload: BlockPayload) => void
}

export const BankAccountsBlock: React.FC<Props> = ({ payload: p, onChange }) => {
  const accounts = p?.accounts ?? []
  const title = p?.title ?? ''

  const sync = (next: Partial<IBlockPayloadBankAccounts>) => {
    onChange({
      title: next.title !== undefined ? next.title : title,
      accounts: next.accounts !== undefined ? next.accounts : accounts,
    })
  }

  const handleAdd = () => {
    sync({ accounts: [...accounts, emptyRow()] })
  }

  const handleRemove = (index: number) => {
    sync({ accounts: accounts.filter((_, i) => i !== index) })
  }

  const handleMove = (index: number, direction: 'up' | 'down') => {
    const target = direction === 'up' ? index - 1 : index + 1
    if (target < 0 || target >= accounts.length) return
    const next = [...accounts]
    ;[next[index], next[target]] = [next[target], next[index]]
    sync({ accounts: next })
  }

  const patchRow = (index: number, patch: Partial<IBankAccountRow>) => {
    const next = accounts.map((row, i) => (i === index ? { ...row, ...patch } : row))
    sync({ accounts: next })
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          textAlign: 'center',
          borderStyle: 'dashed',
          bgcolor: 'rgba(14, 165, 233, 0.06)',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          แสดงบัญชีธนาคารสำหรับโอนเงิน — เพิ่มได้หลายบัญชีในบล็อกเดียว
        </Typography>
      </Paper>

      <Form.Item label="หัวข้อ (ไม่บังคับ)" style={{ marginBottom: 0 }}>
        <Input
          placeholder="เช่น โอนเงินผ่านบัญชี"
          value={title}
          onChange={(e) => sync({ title: e.target.value })}
        />
      </Form.Item>

      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={handleAdd}
        sx={{ alignSelf: 'flex-start' }}
      >
        เพิ่มบัญชี
      </Button>

      {accounts.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Typography variant="caption" fontWeight={600} color="text.secondary">
            รายการบัญชี ({accounts.length})
          </Typography>
          {accounts.map((row, idx) => (
            <Paper
              key={idx}
              variant="outlined"
              sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <span style={{ width: 22, textAlign: 'center', fontSize: 12, color: '#8b949e' }}>
                  {idx + 1}
                </span>
                <IconButton
                  size="small"
                  disabled={idx === 0}
                  onClick={() => handleMove(idx, 'up')}
                  title="เลื่อนขึ้น"
                >
                  <KeyboardArrowUpIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  disabled={idx === accounts.length - 1}
                  onClick={() => handleMove(idx, 'down')}
                  title="เลื่อนลง"
                >
                  <KeyboardArrowDownIcon fontSize="small" />
                </IconButton>
                <Box sx={{ flex: 1 }} />
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleRemove(idx)}
                  title="ลบ"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
              <Form.Item label="ธนาคาร" style={{ marginBottom: 0 }}>
                <Select
                  showSearch
                  allowClear
                  placeholder="เลือกธนาคาร"
                  style={{ width: '100%' }}
                  value={row.bankCode || undefined}
                  onChange={(code: string | null) => {
                    if (!code) {
                      patchRow(idx, { bankCode: undefined, bankName: '' })
                      return
                    }
                    const opt = THAI_BANK_OPTIONS.find((o) => o.code === code)
                    patchRow(idx, {
                      bankCode: code,
                      bankName: opt?.label ?? '',
                    })
                  }}
                  optionFilterProp="label"
                >
                  {THAI_BANK_OPTIONS.map((o) => (
                    <Select.Option key={o.code} value={o.code} label={o.label}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <BankIconImg code={o.code} size={24} />
                        <span>{o.label}</span>
                      </Box>
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item label="ชื่อบัญชี" style={{ marginBottom: 0 }}>
                <Input
                  placeholder="ชื่อบัญชีตามสมุด"
                  value={row.accountName}
                  onChange={(e) => patchRow(idx, { accountName: e.target.value })}
                />
              </Form.Item>
              <Form.Item label="เลขบัญชี" style={{ marginBottom: 0 }}>
                <Input
                  placeholder="เลขบัญชี"
                  value={row.accountNumber}
                  onChange={(e) => patchRow(idx, { accountNumber: e.target.value })}
                />
              </Form.Item>
              <Form.Item label="สาขา (ไม่บังคับ)" style={{ marginBottom: 0 }}>
                <Input
                  placeholder="สาขา"
                  value={row.branch ?? ''}
                  onChange={(e) => patchRow(idx, { branch: e.target.value })}
                />
              </Form.Item>
            </Paper>
          ))}
        </Box>
      )}
    </Box>
  )
}
